/**
 * accountMember.component.tsx
 * Sentriqs
 *
 * Created by: Dylan Knight
 * Copyright (c) 2024 Sentriqs, Inc. All rights reserved.
 *
**/

import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";

import MenuIcon from "@mui/icons-material/Menu";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { Breadcrumbs, Button, IconButton } from "@mui/material"

import { AuthContext } from "../auth/auth.context";
import { AccountNavContext } from "./account.context";
import { AccountGroupDetailComponent } from "./accountGroupDetail.component";
import { AccountGroupMembersComponent } from "./accountGroupMembers.component";
import { Dialog } from "../components/dialog.component";
import { ErrorSnackBar } from "../components/errorSnackBar.component";

import { Group, GroupSubmissionType, GroupType } from "../models/group";

import { groupServiceFactory } from "../services/group.service";

interface ContainerRouteParams {
  groupID: string,
  [key: string]: string | undefined
}

export function AccountGroupContainer(): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const acctContext = React.useContext<AccountNavContext>(AccountNavContext);
  const navigate = useNavigate();
  const { groupID } = useParams<ContainerRouteParams>() as {groupID: string};
  const [errMsg, setErrMsg] = React.useState("");
  const [group, setGroup] = React.useState<Group | null>(null);
  const [showConfirm, setShowConfirm] = React.useState(false);

  React.useEffect(function () {
    const accountID = authContext.membership?.accountID;
    if (!accountID) { return; }
    groupServiceFactory().getGroup(accountID, groupID).then(setGroup, err => {
      console.error("Error fetching group:", err);
      setErrMsg((err as Error).message);
    });
  }, [groupID]);

  async function updateGroupName(accountID: string, groupID: string, groupname: string): Promise<Group | null> {
    try {
      return await groupServiceFactory().updateGroupName(accountID, groupID, {groupname});
    } catch (err) {
      console.error("Error saving group name:", err);
      setErrMsg((err as Error).message);
      return null;
    }
  }

  async function updateGroup(accountID: string, groupID: string, settings: Partial<Group>): Promise<Group | null> {
    try {
      return groupServiceFactory().updateGroupSettings(accountID, groupID, settings);
    } catch (err) {
      console.error("Error saving group update:", err);
      setErrMsg((err as Error).message);
      return group;
    }
  }

  async function deleteGroup(): Promise<void> {
    console.log("delete group");
    if (!authContext.membership) { return; }
    try {
      await groupServiceFactory().deleteGroupFromServer(authContext.membership.accountID, groupID);
      navigate(-1);
    } catch (err) {
      console.error("Error deleting group:", err);
      setErrMsg((err as Error).message);
    }
    setShowConfirm(false);
  }

  return <div className="account-group-container">
    <header className="group-header-nav attached-left-nav">
      <IconButton className="left-nav-outer-toggle" size="small"
          disableRipple disableTouchRipple
          onClick={acctContext.toggleLeftNav}>
        <MenuIcon />
      </IconButton>
      <h3>Group: {group?.groupname || ""}</h3>
    </header>
    <div className="breadcrumb-container">
      <Breadcrumbs aria-label="breadcrumb" className="main-breadcrumbs">
        <Link to="/admin/groups/">
          <NavigateBeforeIcon fontSize="small" />
          <span>Groups</span>
        </Link>
      </Breadcrumbs>
    </div>
    {group && <>
      <div className="form-wrapper">
        <AccountGroupDetailComponent group={group}
          updateGroupName={updateGroupName} updateGroup={updateGroup} />
        <div className="button-row">
          <Button color="error" variant="contained"
          disableRipple disableTouchRipple
          onClick={() => setShowConfirm(true) }>Clear & Delete</Button>
        </div>
      </div>
      </>}
    <section className="group-member-list-container">
      <header className="group-member-section-header"><h2>Members</h2></header>
      <AccountGroupMembersComponent
        accountID={authContext.membership?.accountID || ""}
        groupID={groupID}
        isDM={group ? group.grouptype === GroupType.dm : true }
        submissiontype={group?.submissiontype || GroupSubmissionType.normal}
        onError={err => setErrMsg((err as Error).message)} />
    </section>
    <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("") }/>
    <ConfirmDeleteDialog
      open={showConfirm}
      onConfirm={deleteGroup}
      onClose={() => setShowConfirm(false)} />
  </div>;
}

interface ConfirmProps {
  open: boolean,
  onConfirm(): void,
  onClose(): void
}
function ConfirmDeleteDialog(props: ConfirmProps): React.ReactElement<any, any> {
  return <Dialog open={props.open} loading={false} title="Are you sure?"
      confirmText="Delete" fullWidth={true} onConfirm={props.onConfirm} onClose={props.onClose}>
    
    <p>This action cannot be undone! The system will attempt to clear all content from
      every current member's device and delete the group. Are you sure you want to continue?</p>
  </Dialog>;
}
